import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { postsAPI } from '../services/api';

const ExplorePage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    location: '',
    category: ''
  });

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      try {
        const response = await postsAPI.getAll(filters);
        setPosts(response.data);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        console.error('Error fetching posts:', err);
      }
    };
    fetchPosts();
  }, [filters]);

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters({ ...filters, search: searchQuery });
  };

  const handleLocationFilter = (location) => {
    setFilters({ ...filters, location: location === 'All Areas' ? '' : location });
  };

  // Categorize posts for different sections
  const featuredAttractions = posts.filter(post => 
    post.title === 'Palace of the Parliament' || 
    post.title === 'Romanian Athenaeum' || 
    post.title === 'Village Museum'
  );
  
  const otherPosts = posts.filter(post => !featuredAttractions.find(f => f.post_id === post.post_id));
  
  // Combine to ensure featured attractions are at the top of hiddenGems
  const hiddenGems = [...featuredAttractions, ...otherPosts].slice(0, 3);
  const greenEscapes = posts.filter(post => post.category === 'Nature');
  const tasteOfTown = posts.filter(post => post.category === 'Food & Drink');
  const cultureCommunity = posts.filter(post => post.category === 'Culture' || post.category === 'Entertainment');

  if (loading && posts.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          <p className="mt-4 text-on-surface-variant font-medium">Curating your experience...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background text-on-background antialiased">
      <main>
        {/* Hero Section: The Digital Curator's Welcome */}
        <section className="relative h-[600px] flex items-center justify-center overflow-hidden bg-surface-container-low">
          <div className="absolute inset-0 z-0">
            <img 
              className="w-full h-full object-cover opacity-20 filter grayscale" 
              alt="Atmospheric Bucharest street" 
              src="https://images.unsplash.com/photo-1555990136-2d9340643920?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-secondary/5"></div>
          </div>
          <div className="relative z-10 text-center px-6 max-w-4xl">
            <h1 className="text-6xl md:text-7xl font-bold tracking-tighter text-on-background mb-8 leading-tight">
              Discover Your <br/><span className="text-primary italic">City's Soul with Local Advisor</span>
            </h1>
            
            {/* Search Bar Shell */}
            <form onSubmit={handleSearch} className="relative max-w-2xl mx-auto mt-12">
              <div className="bg-surface-container-lowest p-2 rounded-full shadow-[0_32px_48px_-12px_rgba(18,28,42,0.08)] flex items-center">
                <span className="material-symbols-outlined px-4 text-on-surface-variant">location_on</span>
                <input 
                  className="w-full bg-transparent border-none focus:ring-0 text-lg py-3 font-medium placeholder:text-on-surface-variant/40" 
                  placeholder="Search curated cafes, parks, or galleries..." 
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button type="submit" className="bg-primary text-on-primary px-8 py-3 rounded-full font-bold hover:opacity-90 transition-all">Search</button>
              </div>
            </form>
          </div>
        </section>

        {/* Filters & Navigation */}
        <section className="sticky top-20 z-40 bg-white/60 backdrop-blur-md py-6 shadow-sm">
          <div className="container mx-auto px-12 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="text-xs font-bold uppercase tracking-widest text-on-surface-variant/60">Neighborhoods:</span>
              <div className="flex gap-2">
                {['All Areas', 'Sector 1', 'Sector 2', 'Sector 3', 'Sector 6'].map((loc) => (
                  <span 
                    key={loc}
                    onClick={() => handleLocationFilter(loc)}
                    className={`${(filters.location === loc || (loc === 'All Areas' && !filters.location)) ? 'bg-primary-fixed text-on-primary-fixed' : 'hover:bg-surface-container text-on-surface-variant'} px-4 py-1.5 rounded-full text-sm font-semibold cursor-pointer transition-colors`}
                  >
                    {loc}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs font-bold uppercase tracking-widest text-on-surface-variant/60">Categories:</span>
              <div className="flex gap-2">
                {['Food & Drink', 'Nature', 'Culture', 'Entertainment'].map((cat) => (
                  <span 
                    key={cat}
                    onClick={() => setFilters({ ...filters, category: filters.category === cat ? '' : cat })}
                    className={`${filters.category === cat ? 'bg-secondary-container text-on-secondary' : 'hover:bg-surface-container-high border border-outline-variant/20 text-on-surface-variant'} px-4 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer`}
                  >
                    {cat}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Curated Grid Layout */}
        <section className="container mx-auto px-12 py-24 space-y-32">
          
          {/* Category: The Hidden Gems (Asymmetric Grid) */}
          {hiddenGems.length > 0 && (
            <div>
              <div className="mb-12 flex items-end justify-between">
                <div>
                  <span className="text-primary font-bold tracking-widest uppercase text-sm mb-2 block">Curated Selection</span>
                  <h2 className="text-4xl font-bold tracking-tight text-on-background">The Hidden Gems</h2>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
                {/* Featured Large Card */}
                <div className="md:col-span-8 group relative overflow-hidden rounded-2xl bg-surface-container-lowest shadow-sm hover:shadow-md transition-shadow">
                  <Link to={`/posts/${hiddenGems[0].post_id}`}>
                    <div className="aspect-[16/9] overflow-hidden">
                      <img 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" 
                        alt={hiddenGems[0].title} 
                        src={hiddenGems[0].image_urls?.[0] || "https://images.unsplash.com/photo-1516214104703-d870798883c5?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"}
                      />
                    </div>
                    <div className="p-8">
                      <div className="flex gap-2 mb-4">
                        <span className="text-[10px] font-bold uppercase tracking-widest bg-tertiary-fixed text-on-tertiary-fixed px-2 py-1 rounded">{hiddenGems[0].category}</span>
                        <span className="text-[10px] font-bold uppercase tracking-widest bg-primary-fixed text-on-primary-fixed px-2 py-1 rounded">Featured</span>
                      </div>
                      <h3 className="text-2xl font-bold mb-3">{hiddenGems[0].title}</h3>
                      <p className="text-on-surface-variant font-medium mb-6 leading-relaxed italic line-clamp-2">
                        "{hiddenGems[0].content}"
                      </p>
                      <span className="text-primary font-bold border-b-2 border-primary/20 pb-1 hover:border-primary transition-all">View Details</span>
                    </div>
                  </Link>
                </div>
                {/* Side Secondary Cards */}
                <div className="md:col-span-4 flex flex-col gap-8">
                  {hiddenGems.slice(1, 3).map((post) => (
                    <Link 
                      key={post.post_id} 
                      to={`/posts/${post.post_id}`}
                      className="group bg-surface-container-low rounded-2xl p-6 flex flex-col justify-between hover:shadow-md transition-all"
                    >
                      <div>
                        <div className="aspect-square rounded-xl overflow-hidden mb-6">
                          <img 
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" 
                            alt={post.title} 
                            src={post.image_urls?.[0] || "https://images.unsplash.com/photo-1554118811-1e0d58224f24?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80"}
                          />
                        </div>
                        <h3 className="text-xl font-bold mb-2">{post.title}</h3>
                        <p className="text-sm text-on-surface-variant/80 mb-4 line-clamp-2">{post.content}</p>
                        <div className="flex gap-2">
                          <span className="text-[9px] font-bold uppercase tracking-tighter border border-outline-variant/30 px-2 py-0.5 rounded-full">{post.category}</span>
                          <span className="text-[9px] font-bold uppercase tracking-tighter border border-outline-variant/30 px-2 py-0.5 rounded-full">{post.location_name}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Category: Green Escapes (Bento Layout) */}
          {greenEscapes.length > 0 && (
            <div className="bg-surface-container-low/50 -mx-12 px-12 py-24 rounded-[3rem]">
              <div className="mb-12">
                <span className="text-secondary font-bold tracking-widest uppercase text-sm mb-2 block">Nature's Breath</span>
                <h2 className="text-4xl font-bold tracking-tight text-on-background">Green Escapes</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="md:col-span-2 md:row-span-2 relative group overflow-hidden rounded-2xl aspect-square md:aspect-auto">
                  <Link to={`/posts/${greenEscapes[0].post_id}`}>
                    <img 
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" 
                      alt={greenEscapes[0].title} 
                      src={greenEscapes[0].image_urls?.[0] || "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent p-8 flex flex-col justify-end">
                      <span className="text-primary-fixed font-bold uppercase text-[10px] tracking-widest mb-2">Editor's Pick</span>
                      <h3 className="text-white text-2xl font-bold">{greenEscapes[0].title}</h3>
                      <p className="text-white/80 text-sm mt-2 italic line-clamp-2">"{greenEscapes[0].content}"</p>
                    </div>
                  </Link>
                </div>
                {greenEscapes.slice(1, 4).map((post, index) => (
                  <div key={post.post_id} className={`${index === 0 ? 'md:col-span-2' : 'md:col-span-1'} bg-surface-container-lowest rounded-2xl p-6 group hover:shadow-md transition-shadow`}>
                    <Link to={`/posts/${post.post_id}`}>
                      <div className="aspect-video rounded-lg overflow-hidden mb-4">
                        <img 
                          className="w-full h-full object-cover" 
                          alt={post.title} 
                          src={post.image_urls?.[0] || "https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"}
                        />
                      </div>
                      <h3 className="font-bold text-lg mb-1">{post.title}</h3>
                      <p className="text-xs text-on-surface-variant italic mb-3 line-clamp-1">{post.content}</p>
                      <span className="bg-surface-container px-3 py-1 rounded-full text-[10px] font-bold text-on-surface">EXPLORE</span>
                    </Link>
                  </div>
                ))}
                <div className="md:col-span-1 bg-primary text-on-primary rounded-2xl p-6 flex flex-col justify-center items-center text-center">
                  <span className="material-symbols-outlined text-4xl mb-4">park</span>
                  <h3 className="font-bold">Nature Gems</h3>
                  <p className="text-sm opacity-80">Discover curated green spaces in the city.</p>
                </div>
              </div>
            </div>
          )}

          {/* Category: Taste of the Town (Editorial List) */}
          {tasteOfTown.length > 0 && (
            <div>
              <div className="mb-12 flex items-center justify-between">
                <div>
                  <span className="text-tertiary font-bold tracking-widest uppercase text-sm mb-2 block">Local Palates</span>
                  <h2 className="text-4xl font-bold tracking-tight text-on-background">Taste of the Town</h2>
                </div>
              </div>
              <div className="space-y-4">
                {tasteOfTown.map((post) => (
                  <Link 
                    key={post.post_id} 
                    to={`/posts/${post.post_id}`}
                    className="flex flex-col md:flex-row items-center gap-8 p-6 rounded-2xl hover:bg-surface-container-low transition-colors group"
                  >
                    <div className="w-full md:w-48 aspect-video md:aspect-square rounded-xl overflow-hidden shrink-0">
                      <img 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                        alt={post.title} 
                        src={post.image_urls?.[0] || "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"}
                      />
                    </div>
                    <div className="flex-grow text-center md:text-left">
                      <div className="flex items-center justify-center md:justify-start gap-3 mb-2">
                        <h3 className="text-xl font-bold">{post.title}</h3>
                        <span className="bg-secondary-fixed text-on-secondary-fixed text-[9px] font-extrabold px-2 py-0.5 rounded">POPULAR</span>
                      </div>
                      <p className="text-on-surface-variant max-w-2xl italic line-clamp-2">"{post.content}"</p>
                    </div>
                    <div className="flex flex-wrap gap-2 md:w-48 justify-center md:justify-end">
                      <span className="bg-white/50 px-3 py-1 rounded-full text-[10px] font-bold">Recommended</span>
                      <span className="bg-white/50 px-3 py-1 rounded-full text-[10px] font-bold">{post.location_name}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Category: Culture & Community (Editorial Layout) */}
          {cultureCommunity.length > 0 && (
            <div className="flex flex-col md:flex-row gap-16 items-center">
              <div className="md:w-1/2">
                <span className="text-primary font-bold tracking-widest uppercase text-sm mb-4 block">The Soul of the City</span>
                <h2 className="text-5xl font-bold tracking-tighter leading-tight mb-8">Culture &amp; <br/>Community</h2>
                <p className="text-lg text-on-surface-variant font-medium leading-relaxed mb-10">
                  Beyond the shops and cafes lie the spaces that hold our collective history. From the experimental gallery in the old warehouse to the library that hosts moonlit poetry, these are the anchors of our community.
                </p>
                <div className="grid grid-cols-2 gap-6">
                  {cultureCommunity.slice(0, 4).map((post) => (
                    <Link key={post.post_id} to={`/posts/${post.post_id}`} className="p-6 bg-surface-container rounded-2xl hover:bg-surface-container-high transition-colors">
                      <h4 className="font-bold text-lg mb-1">{post.title}</h4>
                      <p className="text-xs opacity-70 line-clamp-1">{post.location_name}</p>
                    </Link>
                  ))}
                </div>
              </div>
              <div className="md:w-1/2 relative">
                <div className="rounded-[2.5rem] overflow-hidden shadow-2xl rotate-2 hover:rotate-0 transition-transform duration-500">
                  <img 
                    className="w-full h-auto" 
                    alt="Culture section" 
                    src={cultureCommunity[0]?.image_urls?.[0] || "https://images.unsplash.com/photo-1460666819448-6a122e7a1ef8?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"}
                  />
                </div>
                <div className="absolute -bottom-8 -left-8 bg-surface-container-lowest p-6 rounded-2xl shadow-xl max-w-[200px]">
                  <p className="text-sm font-bold text-primary italic leading-tight">"Where every visit feels like a conversation with the artist."</p>
                </div>
              </div>
            </div>
          )}

          {posts.length === 0 && !loading && (
            <div className="text-center py-20">
              <span className="material-symbols-outlined text-6xl text-on-surface-variant/20 mb-4">search_off</span>
              <h3 className="text-2xl font-bold text-on-surface-variant">No gems found yet</h3>
              <p className="text-on-surface-variant/60">Try adjusting your filters to discover something new.</p>
              <button 
                onClick={() => setFilters({ location: '', category: '' })}
                className="mt-6 text-primary font-bold border-b-2 border-primary"
              >
                Clear all filters
              </button>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default ExplorePage;
